import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { extractErrorMessage, useInvalidateQuery } from "@platform/api";
import {
  type AuditFieldOut,
  listAuditFieldsOptions,
  reviewAuditFieldMutation,
} from "@platform/api/golden";
import { Alert, Button, Card, Typography } from "@platform/design-system";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { useGoldenClient } from "../api/GoldenClientProvider";
import { GOLDEN_TAGS } from "../api/goldenTags";
import { EvidenceList } from "../shared/EvidenceList";
import { formatValue } from "../shared/values";

import { BothWrongDialog } from "./BothWrongDialog";
import { SectionStatus } from "./SectionStatus";

import type { ReactNode } from "react";

export const AUDIT_SAMPLE_TEST_ID = "golden-audit-sample";

/**
 * Fields where RUMI and the analyst agreed, sampled to check that agreement is not a
 * shared mistake. A reviewer confirms each, or stores the value both should have had.
 */
export const AuditSample = ({
  caseId,
}: {
  readonly caseId: number;
}): ReactNode => {
  const client = useGoldenClient();
  const invalidateQuery = useInvalidateQuery();
  const audit = useQuery(
    listAuditFieldsOptions({ client, path: { case_id: caseId } }),
  );
  const [correcting, setCorrecting] = useState<AuditFieldOut>();
  const [evidenceError, setEvidenceError] = useState<string>();
  // A both-wrong verdict stores a correction and moves the case's revision.
  const review = useMutation({
    ...reviewAuditFieldMutation({ client }),
    onSuccess: () =>
      Promise.all(
        [GOLDEN_TAGS.reviews, GOLDEN_TAGS.corrections, GOLDEN_TAGS.cases].map(
          (tags) => invalidateQuery({ tags: [...tags] }),
        ),
      ),
  });

  const reviewNotice =
    review.variables?.body.verdict === "both_wrong"
      ? "Correction stored."
      : "Confirmed.";

  return (
    <Card data-testid={AUDIT_SAMPLE_TEST_ID} title="Audit sample">
      <Typography variant="body-medium" color="textSecondary" sx={{ mb: 2 }}>
        Fields where RUMI and the analyst agreed. Check the value against the
        broker documents; RUMI&apos;s evidence says where it read the value.
      </Typography>
      {review.isSuccess && (
        <Alert
          data-testid={`${AUDIT_SAMPLE_TEST_ID}-notice`}
          severity="success"
        >
          {reviewNotice}
        </Alert>
      )}
      {review.isError && (
        <Alert data-testid={`${AUDIT_SAMPLE_TEST_ID}-error`} severity="error">
          {extractErrorMessage(review.error, "Failed to store the verdict")}
        </Alert>
      )}
      {evidenceError && (
        <Alert
          data-testid={`${AUDIT_SAMPLE_TEST_ID}-evidence-error`}
          severity="error"
        >
          {evidenceError}
        </Alert>
      )}
      {audit.isSuccess ? (
        <Table size="small" aria-label="Audit sample">
          <TableHead>
            <TableRow>
              <TableCell>Field</TableCell>
              <TableCell>Agreed value</TableCell>
              <TableCell>RUMI&apos;s evidence</TableCell>
              <TableCell>Verdict</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {audit.data.map((field) => (
              <TableRow
                key={field.id}
                data-testid={`${AUDIT_SAMPLE_TEST_ID}-row-${field.id}`}
              >
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {field.field_path}
                </TableCell>
                <TableCell>{formatValue(field.rumi_value)}</TableCell>
                <TableCell>
                  <EvidenceList
                    caseId={caseId}
                    evidence={field.evidence ?? []}
                    onError={setEvidenceError}
                  />
                </TableCell>
                <TableCell>
                  {field.review_id != null ? (
                    <Typography variant="body-medium" color="textSecondary">
                      Reviewed
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", gap: 1, whiteSpace: "nowrap" }}>
                      <Button
                        variant="secondary"
                        size="sm"
                        data-testid={`${AUDIT_SAMPLE_TEST_ID}-confirm-${field.id}`}
                        disabled={review.isPending}
                        onClick={() =>
                          review.mutate({
                            path: { audit_field_id: field.id },
                            body: { verdict: "confirmed" },
                          })
                        }
                      >
                        Both right
                      </Button>
                      <Button
                        variant="tertiary"
                        size="sm"
                        data-testid={`${AUDIT_SAMPLE_TEST_ID}-both-wrong-${field.id}`}
                        disabled={review.isPending}
                        onClick={() => setCorrecting(field)}
                      >
                        Both wrong…
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {audit.data.length === 0 && (
              <TableRow data-testid={`${AUDIT_SAMPLE_TEST_ID}-empty`}>
                <TableCell colSpan={4}>No audit sample yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      ) : (
        <SectionStatus
          testId={AUDIT_SAMPLE_TEST_ID}
          label="audit sample"
          error={audit.error}
        />
      )}
      <BothWrongDialog
        fieldPath={correcting?.field_path ?? ""}
        open={correcting !== undefined}
        onCancel={() => setCorrecting(undefined)}
        onCorrect={(correctedValue) => {
          if (correcting === undefined) return;
          review.mutate({
            path: { audit_field_id: correcting.id },
            body: { verdict: "both_wrong", corrected_value: correctedValue },
          });
          setCorrecting(undefined);
        }}
      />
    </Card>
  );
};
